import { useEffect, useRef, useState } from 'react';
import './OwnershipWidget.css';
import { getFlagSvg } from '../../Components/Common/Flag/Flag';
import { OwnershipType } from '../../types/ownership.type';
import classNames from 'classnames';
import { formatNumber, formatKMBT } from '../../utils/utils';
import { getOwnership } from '../../api/company.api';
import CommonTooltip from '../../Components/Common/CommonTooltip/CommonTooltip';
import {
  BaseWidgetProps,
  CommonTooltipType,
  WidgetHeightMessage,
} from '../../types/common.type';
import useResizeNotifier from '../../hooks/useResizeNotifier';
import {
  ThemeType,
  WidgetMessageType,
  WidgetNameSource,
} from '../../utils/type';

import Loading from '../../Components/Loading/Loading';
import { ReactComponent as ErrorIcon } from '../../assets/icons/error-state.svg';
import ErrorMessage from '../../Components/ErrorMessage/ErrorMessage';
import { errorMsg } from '../../constants/common.constant';
import Layout from '../../Components/Common/Layout/Layout';

export interface OwnershipWidgetProps extends BaseWidgetProps {
  symbol?: string;
  token?: string;
  onHeightChange?: (message: WidgetHeightMessage) => void;
}

const OwnershipWidget = (props: OwnershipWidgetProps) => {
  const { onHeightChange } = props;
  const containerRef = useRef<HTMLDivElement>(null);
  useResizeNotifier(containerRef, WidgetNameSource.Ownership, onHeightChange);
  const [isLoading, setIsLoading] = useState(true);
  const onLoadingChangeRef = useRef(props.onLoadingChange);
  onLoadingChangeRef.current = props.onLoadingChange;
  useEffect(() => {
    onLoadingChangeRef.current?.(isLoading);
  }, [isLoading]);
  const [isEmptyData, setIsEmptyData] = useState(false);
  const msgItem = errorMsg.find((obj) => obj.key === 'ownership');

  const [ownership, setOwnership] = useState<OwnershipType[]>([]);
  const [mktcap, setMktcap] = useState<string>('-');
  const [outstanding, setOutstanding] = useState<string>('-');
  const [freefloat, setFreefloat] = useState<string>('-');
  const [currency, setCurrency] = useState<string>('-');
  const ownershipTooltipRef = useRef<HTMLDivElement | null>(null);
  const [tooltip, setTooltip] = useState<CommonTooltipType>({
    content: '',
    show: false,
    x: 0,
    y: 0,
  });
  useEffect(() => {
    setIsLoading(true);
    setIsEmptyData(false);
    getOwnership(props.symbol || '', props.token || '')
      .then((res) => {
        if (res.mktcap) {
          setMktcap(res.mktcap);
        }
        if (res.outstanding) {
          setOutstanding(res.outstanding);
        }
        if (res.freefloat) {
          setFreefloat(res.freefloat);
        }
        if (res.currency) {
          setCurrency(res.currency);
        }
        let ownership = res.ownership;
        if (!ownership) {
          setIsEmptyData(true);
          props.onNoData?.({
            type: WidgetMessageType.NoData,
            source: 'OwnershipWidget',
            data: null,
          });
          setIsLoading(false);
          return;
        }
        const arr: OwnershipType[] = [];
        for (const key in ownership) {
          if (ownership[key]) {
            arr.push({
              oid: key,
              oname: ownership[key].oname,
              sharesheld: ownership[key].sharesheld,
              pctoutstanding: ownership[key].pctoutstanding,
              shareschg:
                ownership[key].shareschg === 0
                  ? null
                  : ownership[key].shareschg,
              pctsharesheld:
                ownership[key].pctsharesheld === 0
                  ? null
                  : ownership[key].pctsharesheld,
              reportdate: ownership[key].reportdate,
              newposition: ownership[key].newposition,
            });
          }
        }
        setOwnership(arr);
      })
      .catch((err) => {
        console.log(err);
        props.onError?.({
          message: err.message,
          type: err.name + '_' + err.code,
        });
        setIsEmptyData(true);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [props.symbol]);
  const constrainTooltipPosition = (e: React.MouseEvent) => {
    // Estimate tooltip dimensions (these are approximate values from CSS)
    // Calculate initial position based on item position and mouse offset
    let x = e.pageX + 10;
    let y = e.pageY;

    const elementMaxWidth = ownershipTooltipRef.current?.clientWidth || 0;
    const elementMaxHeight = ownershipTooltipRef.current?.clientHeight || 0;
    const scrollX = window.pageXOffset || document.documentElement.scrollLeft;
    const scrollY = window.pageYOffset || document.documentElement.scrollTop;
    const viewportWidth = window.innerWidth + scrollX;
    const viewportHeight = window.innerHeight + scrollY;

    if (x + elementMaxWidth > viewportWidth) {
      x = x - elementMaxWidth;
    }
    if (y + elementMaxHeight > viewportHeight) {
      y = y - elementMaxHeight;
    }

    return { x, y };
  };

  return (
    <Layout theme={props?.theme || ThemeType.Direct}>
      <div ref={containerRef}>
        {isLoading && !props.onLoadingChange ? (
          <Loading />
        ) : !isLoading && isEmptyData ? (
          <ErrorMessage msg={msgItem} />
        ) : (
          <div className="ownership">
            <div className="ownership-header">Ownership summary</div>
            <div className="ownership-summary">
              <div className="ownership-summary-item mktcap">
                <div className="ownership-summary-item-title">Market cap</div>
                <div className="ownership-summary-item-short-value">
                  {formatKMBT(mktcap, 3, 3, false).value}
                  <span className="currency">
                    {formatKMBT(mktcap, 3, 3, false).suffix} {currency}
                  </span>
                </div>
                <div className="ownership-summary-item-value">
                  {formatNumber(mktcap, 3, 3, false)}
                  <span className="currency">{currency}</span>
                </div>
              </div>
              <div className="ownership-summary-item outstanding">
                <div className="ownership-summary-item-title">Outstanding</div>
                <div className="ownership-summary-item-short-value">
                  {formatKMBT(outstanding, 3, 3, false).value}
                  <span className="unit">
                    {formatKMBT(outstanding, 3, 3, false).suffix}
                  </span>
                </div>
                <div className="ownership-summary-item-value">
                  {formatNumber(outstanding, 0, 0, false)}
                </div>
              </div>
              <div className="ownership-summary-item freefloat">
                <div className="ownership-summary-item-title">Free float</div>
                <div className="ownership-summary-item-short-value">
                  {formatKMBT(freefloat, 3, 3, false).value}
                  <span className="unit">
                    {formatKMBT(freefloat, 3, 3, false).suffix}
                  </span>
                </div>
                <div className="ownership-summary-item-value">
                  {formatNumber(freefloat, 0, 0, false)}
                </div>
              </div>
            </div>
            <table className="ownership-table">
              <thead>
                <tr>
                  <th>Top 10 shareholders</th>
                  <th>
                    <span className="long_num">Number</span>
                    <span className="short_num">Num.</span> of shares (M)
                  </th>
                  <th>Shares changed</th>
                  <th>Filings date</th>
                </tr>
              </thead>

              <div className="ownership-table-spacer"></div>
              <tbody>
                {ownership
                  ? ownership.map((item: OwnershipType) => (
                      <tr key={item.oid}>
                        <td>
                          <div className="info">
                            <span
                              className="index"
                              onMouseEnter={(e) => {
                                const constrainedPosition =
                                  constrainTooltipPosition(e);
                                setTooltip({
                                  show: true,
                                  x: constrainedPosition.x,
                                  y: constrainedPosition.y,
                                  content: item.oname,
                                });
                              }}
                              onMouseMove={(e) => {
                                //if no move, do not update tooltip
                                if (e.movementX === 0 && e.movementY === 0) {
                                  return;
                                }
                                const constrainedPosition =
                                  constrainTooltipPosition(e);
                                setTooltip((prev) => ({
                                  ...prev,
                                  x: constrainedPosition.x,
                                  y: constrainedPosition.y,
                                }));
                              }}
                              onMouseLeave={() => {
                                setTooltip((prev) => ({
                                  ...prev,
                                  show: false,
                                }));
                              }}
                              onMouseOut={() => {
                                setTooltip((prev) => ({
                                  ...prev,
                                  show: false,
                                }));
                              }}
                            >
                              {item.oname}
                            </span>
                          </div>
                        </td>
                        <td>
                          <div className="number-of-shares">
                            <div className="sharesheld">
                              {formatNumber(
                                item.sharesheld,
                                3,
                                3,
                                false,
                                false,
                                false,
                              )}
                            </div>
                            <div className="pctoutstanding">
                              {formatNumber(
                                item.pctoutstanding,
                                2,
                                2,
                                false,
                                false,
                                false,
                                '%',
                              )}
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="shareschg">
                            <div
                              className={classNames('nc', {
                                up: Number(item.shareschg) > 0,
                                down: Number(item.shareschg) < 0,
                              })}
                            >
                              {formatNumber(
                                item.shareschg,
                                0,
                                0,
                                false,
                                true,
                                false,
                              )}
                            </div>
                            {item.newposition === 'Y' ? (
                              <div className="new">NEW</div>
                            ) : (
                              <div
                                className={classNames('pc', {
                                  up: Number(item.pctsharesheld) > 0,
                                  down: Number(item.pctsharesheld) < 0,
                                })}
                              >
                                {formatNumber(
                                  item.pctsharesheld,
                                  2,
                                  2,
                                  false,
                                  true,
                                  false,
                                  '%',
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="reportdate">{item.reportdate}</div>
                        </td>
                      </tr>
                    ))
                  : null}
              </tbody>
            </table>
            <CommonTooltip
              ref={ownershipTooltipRef}
              content={tooltip.content}
              show={tooltip.show}
              x={tooltip.x}
              y={tooltip.y}
            />
          </div>
        )}
      </div>
    </Layout>
  );
};

export default OwnershipWidget;
